import { useEffect, useState } from 'react';
import useLocalStorage from '@afosto/utils-react/useLocalStorage';
import { useAppActions, useAppState } from '@afosto/app-components-react/AppProvider';
import findObjectInArray from '@afosto/utils/array/findObjectInArray';
import { addresses as addressesApi } from '@afosto/contacts/api/Addresses';
import { fulfilment as fulfilmentApi } from '@afosto/fulfilment/api/Fulfilment';
import { FULFILMENT_LOCATIONS_KEY } from './constants';

const useLocationSettings = ({ fetchLocationsEnabled = false }) => {
  const [actions] = useAppActions();
  const { settings } = useAppState();
  const [getLocalStorage, setLocalStorage] = useLocalStorage();
  const [isLoading, setIsLoading] = useState(true);
  const [locationSettings, setLocationSettings] = useState(null);
  const { locations: currentLocations } = settings;

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const { data } = await fulfilmentApi.listFulfilmentLocations();
        const addressPromisses = data.map(location =>
          addressesApi.viewAddress(location.address_id),
        );
        const addressResponses = (
          await Promise.allSettled(addressPromisses).then(result =>
            result.filter(promise => promise.status === 'fulfilled'),
          )
        ).map(response => response.value.data);

        const formattedLocations = data.reduce(
          (acc, item) => [
            ...acc,
            {
              ...item,
              address: findObjectInArray(addressResponses, 'id', item.address_id),
              label: item.name,
              value: item.id,
            },
          ],
          [],
        );
        setLocationSettings(
          formattedLocations.filter(location => currentLocations.includes(location?.id)),
        );
      } catch (error) {
        setLocationSettings([]);
      } finally {
        setIsLoading(false);
      }
    };

    if (fetchLocationsEnabled) {
      fetchLocations();
    }
  }, [fetchLocationsEnabled, getLocalStorage, currentLocations]);

  const saveLocationSettings = locations => {
    const locationIds = (locations ?? []).map(location => location.id);
    setLocationSettings(locations);
    setLocalStorage(FULFILMENT_LOCATIONS_KEY, JSON.stringify(locationIds));
    actions.setSettings({ ...settings, locations: locationIds });
  };

  return {
    isLoading,
    locationSettings,
    saveLocationSettings,
  };
};

export default useLocationSettings;
